import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DomainHealthResult, DomainWarmupItem, Paginated, UpdateDomainWarmupInput, WarmupMessageItem, WarmupPartnerItem, WarmupSettings } from '@crm/shared';
import { api } from '../../api/client';

export const useWarmupDomains = () =>
  useQuery({ queryKey: ['warmup-domains'], queryFn: async () => (await api.get<DomainWarmupItem[]>('/warmup/domains')).data, refetchInterval: 60_000 });
export const useWarmupSettings = () => useQuery({ queryKey: ['warmup-settings'], queryFn: async () => (await api.get<WarmupSettings>('/warmup/settings')).data });
export const useWarmupPartners = () => useQuery({ queryKey: ['warmup-partners'], queryFn: async () => (await api.get<WarmupPartnerItem[]>('/warmup/partners')).data });
export const useWarmupMessages = (params: { domainId?: string; page: number; pageSize: number }) =>
  useQuery({
    queryKey: ['warmup-messages', params],
    queryFn: async () => (await api.get<Paginated<WarmupMessageItem>>('/warmup/messages', { params })).data,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
export const useDomainHealth = (domainId: string | undefined) =>
  useQuery({ queryKey: ['domain-health', domainId], enabled: !!domainId, queryFn: async () => (await api.get<DomainHealthResult | null>(`/warmup/domains/${domainId}/health`)).data });

function useInvalidate(keys: string[][]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}

export function useUpdateDomainWarmup() {
  const inv = useInvalidate([['warmup-domains'], ['campaigns'], ['campaign']]);
  return useMutation({ mutationFn: async ({ id, ...input }: UpdateDomainWarmupInput & { id: string }) => (await api.patch<DomainWarmupItem>(`/warmup/domains/${id}`, input)).data, onSuccess: inv });
}
export function useSaveWarmupSettings() {
  const inv = useInvalidate([['warmup-settings'], ['warmup-domains']]);
  return useMutation({ mutationFn: async (input: WarmupSettings) => (await api.put<WarmupSettings>('/warmup/settings', input)).data, onSuccess: inv });
}
export function usePauseDomain() {
  const inv = useInvalidate([['warmup-domains'], ['campaigns'], ['campaign']]);
  return useMutation({ mutationFn: async ({ id, pause }: { id: string; pause: boolean }) => (await api.post<DomainWarmupItem>(`/warmup/domains/${id}/${pause ? 'pause' : 'resume'}`)).data, onSuccess: inv });
}
export function useAddWarmupPartners() {
  const inv = useInvalidate([['warmup-partners']]);
  return useMutation({ mutationFn: async (text: string) => (await api.post<{ added: number; existing: number; agents: number; invalid: string[] }>('/warmup/partners', { text })).data, onSuccess: inv });
}
export function useUpdateWarmupPartner() {
  const inv = useInvalidate([['warmup-partners']]);
  return useMutation({ mutationFn: async ({ id, ...input }: { id: string; active?: boolean; name?: string | null }) => (await api.patch<WarmupPartnerItem>(`/warmup/partners/${id}`, input)).data, onSuccess: inv });
}
export function useDeleteWarmupPartner() {
  const inv = useInvalidate([['warmup-partners']]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/warmup/partners/${id}`)).data, onSuccess: inv });
}
export function useSendWarmupNow() {
  const inv = useInvalidate([['warmup-messages'], ['warmup-partners'], ['warmup-domains']]);
  return useMutation({ mutationFn: async (input: { agentId: string; partnerId?: string }) => (await api.post<{ id: string; to: string; subject: string }>('/warmup/send-now', input)).data, onSuccess: inv });
}
export function useRunDomainHealth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (domainId: string) => (await api.post<DomainHealthResult>(`/warmup/domains/${domainId}/health`)).data,
    onSuccess: (data) => {
      qc.setQueryData(['domain-health', data.domainId], data);
      void qc.invalidateQueries({ queryKey: ['warmup-domains'] });
    },
  });
}
export function useDomainTestEmail() {
  return useMutation({ mutationFn: async (input: { agentId: string; to: string }) => (await api.post<{ id: string; to: string; subject: string }>('/warmup/test-email', input)).data });
}
